import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../app/api-client';
import { paymentProvidersKey } from './queries';
import type { PaymentProviderDto, ProviderFormValues } from './schema';

function invalidateProviders(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: [...paymentProvidersKey, 'list'] });
}

export function useCreateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProviderFormValues) =>
      api.post('admin/payments/providers', { json: body }).json<PaymentProviderDto>(),
    onSuccess: () => invalidateProviders(qc),
  });
}

export function useUpdateProvider(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProviderFormValues) =>
      api.put(`admin/payments/providers/${id}`, { json: body }).json<PaymentProviderDto>(),
    onSuccess: () => invalidateProviders(qc),
  });
}

export function useDeleteProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`admin/payments/providers/${id}`).json<{ ok: boolean }>(),
    onSuccess: () => invalidateProviders(qc),
  });
}

export function useUpsertCityLink(cityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { cityId: string; providerId: string; priority: number; isActive: boolean }) =>
      api.post('admin/payments/city-links', { json: body }).json<unknown>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...paymentProvidersKey, 'city-links', cityId] }),
  });
}

export function useDeleteCityLink(cityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (providerId: string) =>
      api.delete(`admin/payments/city-links/${cityId}/${providerId}`).json<{ ok: boolean }>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...paymentProvidersKey, 'city-links', cityId] }),
  });
}
